/**
 * useAdminProjectsWithMembers — the data layer for the /admin/projects page.
 *
 * READ-ONLY under RBAC v2: reads the org-scoped projects-with-members
 * snapshot. The old matrix-cell mutator is gone with its endpoints —
 * membership is mutated only by each project's owning planner
 * (docs/rbac-v2-spec.md rules 4 & 6).
 *
 * Shape consumed from GET /api/admin/projects-with-members (the field-naming law):
 *   { total, projects: [ { id, name, code, status, member_count,
 *       members: [ { user_id, name, email, org_role, project_role, assigned_at } ] } ] }
 *
 * intent (from accessIntentForStatus on the read error status):
 *   ALLOW   — data loaded
 *   DISABLE — 403, org-role member hitting an admin-only endpoint => render an
 *             access-restricted state; the page surfaces this.
 *   HIDE    — 404 (not expected here, but mapped for completeness)
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { accessIntentForStatus, ACCESS_INTENT } from '../lib/auth';

export function useAdminProjectsWithMembers() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [intent, setIntent] = useState(ACCESS_INTENT.ALLOW);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/projects-with-members');
      setProjects(res.data?.projects || []);
      setIntent(ACCESS_INTENT.ALLOW);
    } catch (error) {
      const status = error?.response?.status;
      // 403 => org-role member: restricted. 404 => hide. Else allow (and empty).
      setIntent(accessIntentForStatus(status));
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { projects, loading, intent, refetch };
}

export default useAdminProjectsWithMembers;
